import { createFileRoute, Link } from "@tanstack/react-router";
import { Compass, BarChart3, Shield, Layers, Users, Lightbulb, ArrowRight } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";

export const Route = createFileRoute("/services/tech-consultation/")({
  component: TechIndex,
  head: () => ({
    meta: [
      { title: "Tech Consultation — CoderLity" },
      { name: "description", content: "Expert guidance on architecture, cloud, security and engineering strategy." },
      { property: "og:title", content: "Tech Consultation — CoderLity" },
      { property: "og:description", content: "Senior judgment for architecture, scaling, security and team strategy without hiring a full-time CTO." },
      { property: "og:url", content: "/services/tech-consultation" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/services/tech-consultation" }],
  }),
});

const strategy = [
  { slug: "product-discovery", title: "Product Discovery", desc: "Explore product discovery" },
  { slug: "mvp-planning", title: "MVP Planning", desc: "Explore mvp planning" },
  { slug: "tech-stack-audit", title: "Tech Stack Audit", desc: "Explore tech stack audit" },
];

const architecture = [
  { slug: "cloud-architecture", title: "Cloud Architecture", desc: "Explore cloud architecture" },
  { slug: "microservices", title: "Microservices", desc: "Explore microservices" },
  { slug: "devops-cicd", title: "DevOps & CI/CD", desc: "Explore devops & ci/cd" },
];

const security = [
  { slug: "security-audit", title: "Security Audit", desc: "Explore security audit" },
  { slug: "compliance-gdpr", title: "Compliance (GDPR)", desc: "Explore compliance (gdpr)" },
];

function Group({ title, items }: { title: string; items: { slug: string; title: string; desc: string }[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <Link
              key={it.slug}
              to="/services/tech-consultation/$child"
              params={{ child: it.slug }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
            >
              <h3 className="text-lg font-semibold">{it.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function TechIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Strategy" items={strategy} />
        <Group title="Architecture" items={architecture} />
        <Group title="Security" items={security} />
      </PageShell>
    </>
  );
}

const data = {
  eyebrow: "Services",
  title: "Tech Consultation",
  sub: "Expert guidance on architecture and strategy.",
  intro:
    "When you need senior judgment without hiring a full-time CTO, we plug in. From architecture reviews to technical due diligence, from cloud cost audits to scaling roadmaps — we bring 15+ years of shipping systems at scale to your toughest decisions. Engagements range from one-week audits to fractional CTO retainers.",
  features: [
    { icon: Compass, title: "Architecture review", description: "Independent assessment of your stack, data model and scaling path." },
    { icon: BarChart3, title: "Cloud cost audit", description: "Identify waste in AWS, GCP or Azure and cut bills 30–60%." },
    { icon: Shield, title: "Security review", description: "Threat modeling, OWASP audit and compliance gap analysis." },
    { icon: Layers, title: "Technical due diligence", description: "Pre-investment or pre-acquisition deep dives for VCs and acquirers." },
    { icon: Users, title: "Team scaling", description: "Hiring plans, org structure and engineering culture playbooks." },
    { icon: Lightbulb, title: "Innovation workshops", description: "AI/ML, edge compute and emerging-tech opportunity mapping." },
  ],
  process: [
    { step: "01", title: "Scope", description: "30-min call to align on goals, constraints and timeline." },
    { step: "02", title: "Deep dive", description: "Code reviews, stakeholder interviews and metric analysis." },
    { step: "03", title: "Report", description: "Findings, prioritized recommendations and ROI estimates." },
    { step: "04", title: "Execute", description: "Hands-on support to implement the highest-impact changes." },
  ],
  deliverables: [
    "Written report with findings and recommendations",
    "Prioritized roadmap with effort and impact estimates",
    "Architecture diagrams and decision records",
    "Live walkthrough with your team and stakeholders",
    "Follow-up Q&A for 30 days",
    "Optional implementation support",
  ],
  pricing: [
    { name: "Audit", price: "$5k", features: ["1-week deep dive", "Written report", "Live walkthrough", "30-day Q&A"] },
    { name: "Strategy sprint", price: "$15k", period: "project", highlighted: true, features: ["3-week engagement", "Architecture + roadmap", "Stakeholder workshops", "Decision records"] },
    { name: "Fractional CTO", price: "$10k", period: "month", features: ["2 days per week", "Hiring + technical leadership", "Board reporting", "Vendor management"] },
  ],
  faqs: [
    { q: "Do you sign NDAs?", a: "Always. We sign your NDA before any deep technical discussion and can also provide our own." },
    { q: "Will you recommend your own services?", a: "We surface what's best for you — sometimes that means hiring in-house, picking a competitor, or doing nothing. Independence is the whole point." },
    { q: "Can you help us hire?", a: "Yes. We've helped 30+ teams design hiring rubrics, write job specs and run technical interviews." },
    { q: "What about due diligence for investors?", a: "We do tech DD for VC and PE firms — typically a 1–2 week engagement with a confidential report for the deal team." },
  ],
};
